import { ComponentDataDestService } from '../component-data-dest.service';
import { ComponentDataSourceService } from '../../component-data-source/component-data-source.service';
import { catchError, map, switchMap } from 'rxjs';
import { environment } from '../../../environment/environment';

interface ApiResponse {
    data: {
        module_id: number;
        [key: string]: any;
    };
}

interface Description {
    language_code: string;
    translation_text: string;
}

function validateDescriptions(descriptions: any): void {
    if (!descriptions || typeof descriptions !== 'object') {
        throw new Error('Descriptions must be an object');
    }
    
    for (const [lang, desc] of Object.entries(descriptions)) {
        if (!desc || typeof desc !== 'object') {
            throw new Error(`Description for language ${lang} must be an object`);
        }
        if (!('language_code' in desc) || !('translation_text' in desc)) {
            throw new Error(`Description for language ${lang} must have language_code and translation_text`);
        }
    }
}

function validateMaintenanceMinutes(minutes: any): number {
    const num = Number(minutes);
    if (isNaN(num) || num < 0) {
        throw new Error('Maintenance minutes must be a non-negative number');
    }
    return num;
}

function validateSubtypes(subtypes: any): { subtype_id: number }[] {
    
    if (!subtypes) {
        return [];
    }
    
    // Handle case where subtypes is an object from dual panel selector
    // e.g. { event_subtypes: [1, 2, 3] }
    if (typeof subtypes === 'object' && !Array.isArray(subtypes)) {
        // If it's an object with array properties, try to extract the first array found
        const arrayProps = Object.values(subtypes).filter(val => Array.isArray(val));
        if (arrayProps.length > 0) {
            subtypes = arrayProps[0];
        }
    }
    
    if (!Array.isArray(subtypes)) {
        throw new Error('Subtypes must be an array');
    }
    
    const validatedSubtypes = subtypes.map(subtype => {
        // Handle both number and object formats
        const id = typeof subtype === 'object' ? subtype.subtype_id : subtype;
        const numId = Number(id);
        
        if (isNaN(numId) || numId <= 0) {
            throw new Error(`Invalid subtype ID: ${id}`);
        }
        return { subtype_id: numId };
    });
    
    return validatedSubtypes;
}

export function add_module(componentDataDestService: ComponentDataDestService, componentDataSourceService: ComponentDataSourceService, data: any) {
    
    // Validate required fields
    if (!data.name || !data.visibility) {
        throw new Error('Name and visibility are required fields');
    }
    
    // Step 1: Create base module (without subtypes)
    const baseModuleData = {
        module: {
            name: data.name,
            visibility: data.visibility
        }
    };

    // Send base module to API
    const request$ = componentDataDestService.http.post<ApiResponse>(`${environment.apiUrl}/events/modules`, baseModuleData).pipe(
        catchError(error => {
            console.error("Error creating base module:", error);
            throw error;
        }),
        switchMap(response => {
            const moduleId = response.data.module_id;
            
            // Step 2: Create version if version data is provided
            if (data.version_start_date) {
                const startDate = new Date(data.version_start_date);
                const endDate = data.version_end_date ? new Date(data.version_end_date) : undefined;
                
                // Validate dates
                if (isNaN(startDate.getTime())) {
                    throw new Error('Invalid start date format');
                }
                if (endDate && isNaN(endDate.getTime())) {
                    throw new Error('Invalid end date format');
                }

                // Validate descriptions if provided
                if (data.descriptions) {
                    validateDescriptions(data.descriptions);
                }

                // Log subtypes data for debugging
                if (data.subtypes) {
                    if (typeof data.subtypes === 'object') {
                    }
                }

                // Prepare version data with subtypes
                let validatedSubtypes = [];
                try {
                    validatedSubtypes = data.subtypes ? validateSubtypes(data.subtypes) : [];
                } catch (error) {
                    console.error("Error validating subtypes:", error);
                    console.error("Subtypes data that caused error:", JSON.stringify(data.subtypes, null, 2));
                    throw error;
                }

                const versionData = {
                    valid_from: startDate.toISOString().split('T')[0],
                    valid_to: endDate ? endDate.toISOString().split('T')[0] : null,
                    maintenance_minutes: validateMaintenanceMinutes(data.maintenance_minutes),
                    descriptions: data.descriptions,
                    subtypes: validatedSubtypes
                };

                // Create version using the module ID from the response
                return componentDataDestService.http.post<ApiResponse>(
                    `${environment.apiUrl}/events/modules/${moduleId}/versions`,
                    versionData
                ).pipe(
                    catchError(error => {
                        console.error("Error creating module version:", error);
                        throw error;
                    }),
                    map(versionResponse => ({
                        ...response,
                        version: versionResponse
                    }))
                );
            }
            return [response];
        })
    );

    // Use the helper function to handle store update
    componentDataSourceService.updateStoreData(request$, 'modules').subscribe({
        error: (error) => console.error('Subscription error:', error)
    });
} 